/**
 * Config que o APK host injeta no bundle (embedded).
 * ============================================================================
 *
 * O AAR escreve `window.__effluxEmbedded` em **document-start** — antes do
 * primeiro script do bundle — então tudo aqui já está disponível na avaliação
 * dos módulos, inclusive nos `const` de topo de arquivo.
 *
 * ```js
 * window.__effluxEmbedded = { externalId, apiUrl?, tenant?, aarVersion? }
 * ```
 *
 * **Por que este módulo existe.** Até 2026-09-22 o `apiUrl` do `EffluxConfig`
 * era decorativo: viajava no payload do comando `start`, o handler o descartava,
 * e as ~44 leituras de `import.meta.env.VITE_*_URL` espalhadas pelo player
 * continuavam mandando tudo pra `api.efflux.media` assada no build. Um parceiro
 * apontando o AAR pro próprio servidor via o player ignorar o endereço.
 *
 * A regra agora é uma só: **se o host passou `apiUrl`, ele manda em TUDO**
 * (sync, SSE, analytics, RemoteOps, resolução de URL relativa, telas de
 * diagnóstico). Sem `apiUrl`, nada muda — vale o que veio do build.
 *
 * Fora do embedded (`__effluxEmbedded` ausente) toda função aqui devolve `null`
 * e os chamadores caem exatamente na expressão que usavam antes. Android, Tizen,
 * webOS e browser não enxergam diferença.
 *
 * O `apiUrl` é lido a cada chamada, e não cacheado: o AAR pode reescrever o
 * marcador quando o host chama `start` com endereço diferente.
 */

interface MarcadorEmbedded {
  externalId?: string;
  apiUrl?: string;
  tenant?: string;
  /** Versão do AAR que hospeda este bundle (desde o AAR 0.4.0). */
  aarVersion?: string;
}

function marcador(): MarcadorEmbedded | null {
  if (typeof window === 'undefined') return null;
  const bruto = (window as Window & { __effluxEmbedded?: unknown })
    .__effluxEmbedded;
  // O marcador legado era `{externalId}` apenas — objeto sem `apiUrl` é normal.
  return bruto && typeof bruto === 'object'
    ? (bruto as MarcadorEmbedded)
    : null;
}

function texto(valor: unknown): string | null {
  if (typeof valor !== 'string') return null;
  const limpo = valor.trim();
  return limpo.length > 0 ? limpo : null;
}

/**
 * Base da API que o host passou, ou `null` fora do embedded / sem o parâmetro.
 *
 * Use quando o chamador tem uma cadeia de fallback própria que precisa ser
 * preservada — `hostApiUrl() ?? <expressão original>`. Quando a cadeia é a
 * comum, prefira {@link resolveApiBaseUrl}.
 */
export function hostApiUrl(): string | null {
  const url = texto(marcador()?.apiUrl);
  // Barra final some: o código concatena `${base}/api/...` em dezenas de
  // lugares, e `https://host//api` quebra roteamento em alguns servidores.
  return url ? url.replace(/\/+$/, '') : null;
}

/** Tenant que o host passou, ou `null`. Vide {@link resolveTenantSlug}. */
export function hostTenantSlug(): string | null {
  return texto(marcador()?.tenant);
}

/**
 * Base da API para o caso comum: host primeiro, build depois.
 *
 * @param fallback usado quando nem o host nem o build definiram nada.
 */
export function resolveApiBaseUrl(fallback = 'http://localhost:8082'): string {
  return (
    hostApiUrl() ||
    import.meta.env?.VITE_CONTENT_API_URL ||
    import.meta.env?.VITE_SSE_API_URL ||
    import.meta.env?.VITE_API_URL ||
    fallback
  );
}

/**
 * Base do SSE. Mesma regra, com a cadeia de build invertida (SSE antes de
 * content) — é a ordem que o `useSyncInit` sempre usou, preservada de propósito
 * para ambientes que separam os dois endpoints.
 */
export function resolveSseBaseUrl(fallback = 'http://localhost:8082'): string {
  return (
    hostApiUrl() ||
    import.meta.env?.VITE_SSE_API_URL ||
    import.meta.env?.VITE_CONTENT_API_URL ||
    fallback
  );
}

/** Slug do tenant: host primeiro, build depois. */
export function resolveTenantSlug(fallback = 'efflux'): string {
  return hostTenantSlug() || import.meta.env?.VITE_TENANT_SLUG || fallback;
}

/**
 * Versão do AAR que hospeda este bundle — `null` fora do embedded, ou num AAR anterior
 * ao 0.4.0 (que não mandava o campo).
 *
 * <p>Existe porque o APK carrega DUAS versões com ciclos diferentes: o bundle JS, que
 * atualiza por OTA em minutos, e o nativo, que só muda quando o parceiro recompila. Sem
 * este campo o admin via só a do bundle — e não havia como responder "esta loja já está
 * com o AAR corrigido?" senão inferindo pelo comportamento no log, na mão.</p>
 *
 * <p>`null` num aparelho embedded é informação, não ausência: significa **AAR anterior
 * ao 0.4.0**.</p>
 */
export function hostAarVersion(): string | null {
  return texto(marcador()?.aarVersion);
}
